import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import React, { useState, useMemo } from "react";
import { Run } from "../../services/appsync/types";
import dayjs from "../../utils/dayjs";
import { DataPullsPanelContainer } from "./data_pulls_panel/container";
import { RunNameContainer } from "./run_name/container";
import { useGuardedNavigation } from "../../hooks/use_guarded_navigation";
import {
  convertToClientFeatures,
  type ClientFeatureFlags,
} from "../../types/feature_flags";
import {
  useAdmin,
  useRefetchCustomerSettings,
  useCustomer,
} from "../../contexts/app/provider";
import { useContext } from "react";
import { LockForecastContext } from "../../contexts/lock_forecast";

const RunHistoryRow = (props: {
  run: Run;
  selectedRunId: string | null;
  setMainRun: (run: Run) => void;
  onSettingsChange: () => void;
  customerSettings: any;
  isAdmin: boolean;
}) => {
  const {
    run,
    selectedRunId,
    setMainRun,
    customerSettings,
    onSettingsChange,
    isAdmin,
  } = props;
  const [open, setOpen] = useState(false);
  const { navigateWithGuards } = useGuardedNavigation();
  const features: ClientFeatureFlags = useMemo(
    () => convertToClientFeatures(customerSettings?.features),
    [customerSettings]
  );
  const { lockForecast, unlockForecast, isLockUnlockLoading } =
    useContext(LockForecastContext);

  // Check if the current run is the one that locked the forecast
  const isLocked = useMemo(() => {
    if (!customerSettings) {
      return false;
    }

    const forecastLockedToRunId = customerSettings.forecastLockedToRunId;
    if (!forecastLockedToRunId) {
      return false;
    }

    // Check if the current run's settings match the locked run ID
    return JSON.stringify(run.runSettings) === forecastLockedToRunId;
  }, [run.runSettings, customerSettings]);

  const handleCheckboxClick = async () => {
    console.log("[RunHistoryRow] handleCheckboxClick called", {
      runId: run.id,
      displayId: run.displayId,
      isLocked,
      isLockUnlockLoading,
      runSettings: run.runSettings,
      timestamp: new Date().toISOString(),
    });

    if (isLocked) {
      // If already locked, unlock it
      console.log("[RunHistoryRow] Unlocking forecast for run", run.displayId);
      await unlockForecast();
      onSettingsChange();
    } else {
      // If not locked, lock it
      console.log("[RunHistoryRow] Locking forecast for run", {
        displayId: run.displayId,
        inputId: run.runSettings.inputId,
        settingsNameAndVersion: run.runSettings.settingsNameAndVersion,
      });
      await lockForecast(
        run.runSettings.inputId,
        run.runSettings.settingsNameAndVersion,
        JSON.stringify(run.runSettings)
      );
      onSettingsChange();
    }
  };

  return (
    <React.Fragment>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          borderTop: "none",
        }}
      >
        <TableCell sx={{ borderBottom: "unset" }}>{run.displayId}</TableCell>
        {isAdmin && (
          <TableCell
            sx={{
              borderBottom: "unset",
              textAlign: "center",
              verticalAlign: "middle",
            }}
          >
            <Checkbox
              checked={isLocked}
              disabled={isLockUnlockLoading}
              size="small"
              onClick={handleCheckboxClick}
            />
          </TableCell>
        )}
        <TableCell sx={{ borderBottom: "unset" }}>
          {dayjs.utc(run?.createdAt).local().format("MM/DD/YYYY HH:mm")}
        </TableCell>
        <TableCell sx={{ borderBottom: "unset" }}>
          {run.status === "RUN_IN_PROGRESS" && (
            <Chip
              size="small"
              variant="outlined"
              label="In Progress"
              color="info"
            />
          )}
          {run.status === "RUN_COMPLETED" && (
            <Chip
              size="small"
              variant="outlined"
              label="Completed"
              color="success"
            />
          )}
          {run.status === "RUN_FAILED" && (
            <Chip
              size="small"
              variant="outlined"
              label="Failed"
              color="error"
            />
          )}
          {run.status === "CANCEL_IN_PROGRESS" && (
            <Chip
              size="small"
              variant="outlined"
              label="Cancelling"
              color="warning"
            />
          )}
          {run.status === "CANCEL_COMPLETED" && (
            <Chip
              size="small"
              variant="outlined"
              label="Cancelled"
              color="info"
            />
          )}
          {run.status === "CANCEL_FAILED" && (
            <Chip
              size="small"
              variant="outlined"
              label="Cancel Failed"
              color="error"
            />
          )}
        </TableCell>
        <TableCell sx={{ borderBottom: "unset" }}>
          <Tooltip
            title={
              "Date of update:" +
              dayjs
                .utc(run.runSettings.settingsNameAndVersion.split("|")[1])
                .local()
                .format("MM/DD/YYYY HH:mm")
            }
          >
            <Chip
              size="small"
              variant="outlined"
              label={run.runSettings.settingsNameAndVersion.split("|")[0]}
            />
          </Tooltip>
        </TableCell>
        <TableCell sx={{ borderBottom: "unset" }}>
          {run.initiatedBy || ""}
        </TableCell>
        <TableCell sx={{ borderBottom: "unset" }}>
          <RunNameContainer run={run} />
        </TableCell>
        <TableCell sx={{ borderBottom: "unset" }}>
          <Button
            variant="outlined"
            size="small"
            disabled={run.id === selectedRunId}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setMainRun(run);
            }}
          >
            {selectedRunId === run.id ? "Current" : "Select"}
          </Button>
        </TableCell>
        <TableCell sx={{ borderBottom: "unset" }} align="right">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow sx={{ borderTop: "none" }}>
        <TableCell
          sx={{ paddingBottom: 0, paddingTop: 0, borderTop: "none" }}
          colSpan={isAdmin ? 9 : 8}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                margin: 1,
                borderTop: "1px solid #efefef",
                display: "flex",
                alignItems: "center",
                gap: 4,
                flexWrap: "wrap",
                py: 2,
                px: 1,
              }}
            >
              <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                Started{" "}
                <b>
                  {dayjs.utc(run.createdAt).local().format("MM/DD/YYYY HH:mm")}
                </b>
              </Box>
              <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                Last update{" "}
                <b>
                  {dayjs.utc(run.updatedAt).local().format("MM/DD/YYYY HH:mm")}
                </b>
              </Box>
              <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                Initiated by <b>{run.initiatedBy || "—"}</b>
              </Box>
              <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                Settings{" "}
                <b>{run.runSettings.settingsNameAndVersion.split("|")[0]}</b>
              </Box>
              {run.description && (
                <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                  {run.description}
                </Box>
              )}
              <Box sx={{ flex: 1 }} />
              {features?.RunReportCard && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<InsightsOutlinedIcon />}
                  onClick={() =>
                    navigateWithGuards(`/run-report-card?run=${run.id}`)
                  }
                >
                  View report card
                </Button>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export const RunHistoryComponent = (props: {
  loading: boolean;
  runs: Run[];
  mainRun: Run | null;
  setMainRun: (run: Run) => void;
  lastSuccessfulRun: Run | null;
}) => {
  const { runs, mainRun, setMainRun, lastSuccessfulRun } = props;
  const { isAdmin } = useAdmin();
  const { refetchCustomerSettings } = useRefetchCustomerSettings();
  const { customerSettings } = useCustomer();

  const handleSettingsChange = () => {
    refetchCustomerSettings();
  };

  const selectedRunId = (mainRun || lastSuccessfulRun)?.id || null;

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell />
              {isAdmin && (
                <TableCell
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  <Tooltip title="Select which run's forecast you would like to lock for all future runs.">
                    <LockOutlinedIcon sx={{ fontSize: 20, mt: 1 }} />
                  </Tooltip>
                </TableCell>
              )}
              <TableCell>Started At</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Settings Name</TableCell>
              <TableCell>Initiated By</TableCell>
              <TableCell>Run Name</TableCell>
              <TableCell>Set as Current</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run) => (
              <RunHistoryRow
                key={run.id}
                run={run}
                selectedRunId={selectedRunId}
                setMainRun={setMainRun}
                onSettingsChange={handleSettingsChange}
                customerSettings={customerSettings}
                isAdmin={isAdmin}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <DataPullsPanelContainer />
    </Box>
  );
};
